<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchEra,
  fetchLeaderboard,
  type EraView,
  type LeaderboardView,
  type LeaderboardRow,
} from '../game/rankings-api';
import { BG, rankMedal } from '../game/art';
import { toast } from '../game/toast';
import { t } from '../i18n';

const loading = ref(true);
const error = ref('');

const era = ref<EraView | null>(null);
const board = ref<LeaderboardView | null>(null);
const category = ref('era-points');
const boardLoading = ref(false);

/** Every number on this screen goes through here, so it is deliberately
 *  defensive: a missing or non-finite standing must read as 0, never "NaN"
 *  (QA 2026-08-17 saw NaN on a board where the player had no points yet). */
const fmt = (n: number | null | undefined): string =>
  (typeof n === 'number' && Number.isFinite(n) ? n : 0).toLocaleString();

/** Label of the active category — the score's unit line on each stone. */
const activeCatLabel = computed(
  () => board.value?.categories.find((c) => c.key === category.value)?.label ?? '',
);

/** Purely presentational: which height step a stone in the ladder takes.
 *  Only the top three stand taller; every stone below settles to one height,
 *  so a fifty-row board never tapers a stump down to nothing. */
const stoneClass = (rank: number): string =>
  rank === 1 ? 'rk-line--top1' : rank === 2 ? 'rk-line--top2' : rank === 3 ? 'rk-line--top3' : 'rk-line--settled';

// ── Live era clock ──────────────────────────────────────────────────────────
const now = ref(Date.now());
let tick: number | undefined;
onMounted(() => { tick = window.setInterval(() => { now.value = Date.now(); }, 1000); });
onUnmounted(() => { if (tick) window.clearInterval(tick); });

const eraClock = computed(() => {
  if (!era.value) return '';
  const ms = new Date(era.value.era.endsAt).getTime() - now.value;
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = String(Math.floor((s % 86400) / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return d > 0 ? `${d}d ${h}:${m}:${sec}` : `${h}:${m}:${sec}`;
});

// ── The tier ladder — full EraView.tiers, finally on display ────────────────
const ladder = computed(() => {
  if (!era.value) return [];
  return [...era.value.tiers].sort((a, b) => a.minPoints - b.minPoints);
});
/** Nodes sit at EQUAL intervals (thresholds are wildly uneven — a linear
 *  scale piles the early tiers into the first 8% of the rail). The player
 *  marker interpolates piecewise between the two thresholds it sits between,
 *  so geometry stays honest per segment. */
const tierPctAt = (ti: number) =>
  ladder.value.length > 1 ? Math.round((ti / (ladder.value.length - 1)) * 100) : 0;
const railPct = computed(() => {
  const l = ladder.value;
  if (!era.value || l.length < 2) return 0;
  const pts = era.value.player.rankPoints;
  if (pts >= l[l.length - 1].minPoints) return 100;
  let i = 0;
  while (i < l.length - 1 && pts >= l[i + 1].minPoints) i++;
  const lo = l[i].minPoints, hi = l[i + 1].minPoints;
  const frac = hi > lo ? (pts - lo) / (hi - lo) : 0;
  return Math.round(((i + frac) / (l.length - 1)) * 100);
});
const reachedTier = (min: number) => (era.value?.player.rankPoints ?? 0) >= min;

// ── Podium & ledger split ───────────────────────────────────────────────────
const podium = computed<LeaderboardRow[]>(() => board.value?.top.slice(0, 3) ?? []);
const ledger = computed(() => board.value?.top.slice(3) ?? []);
const meInTop = computed(() => !!board.value?.top.some((r) => r.isMe));
/** …, 635, 636(me), 637 — the server sends this only when I rank past `top`. */
const around = computed<LeaderboardRow[]>(() => board.value?.around ?? []);
/** No window and not in `top` = no standing on this board yet. */
const unranked = computed(() => !!board.value && !meInTop.value && !around.value.length);

async function loadBoard(cat: string) {
  category.value = cat;
  boardLoading.value = true;
  error.value = '';
  try {
    board.value = await fetchLeaderboard(cat);
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    boardLoading.value = false;
  }
}

onMounted(async () => {
  try {
    era.value = await fetchEra();
    // Promotions paid BY this read (lazy, once per tier per saga) — say so loudly.
    for (const p of era.value?.newlyPromoted ?? []) {
      const runes = p.premium ? ` + ${p.premium} runes` : '';
      toast(t('rankings.promoted', { tier: p.tier, silver: p.soft.toLocaleString(), runes }));
    }
    await loadBoard('era-points');
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section>
    <!-- banner -->
    <header class="scr-head">
      <span class="scr-head-win"><img :src="BG.rankings" alt="" /></span>
      <div class="scr-head-txt">
        <h1>{{ t('rankings.title') }}</h1>
        <p>{{ t('rankings.sub') }}</p>
      </div>
    </header>

    <p v-if="loading" class="text-text-dim">{{ t('rankings.loading') }}</p>
    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>

    <div v-if="!loading" class="grid gap-8">
      <!-- ── THE ERA — tier ladder ─────────────────────────────────── -->
      <div v-if="era" class="rk-era bound-frame relative">

        <div class="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div class="flex items-center gap-3">
            <h2 class="font-heading text-xl text-gold">{{ era.era.name }}</h2>
            <span class="rk-chip">{{ t('rankings.saga', { n: era.era.number }) }}</span>
            <span class="rk-chip rk-chip-doom">{{ t('rankings.ragnarok', { time: eraClock }) }}</span>
          </div>
          <div class="text-right">
            <span class="font-heading text-2xl text-gold-bright tabular-nums">{{ fmt(era.player.rankPoints) }}</span>
            <span class="font-label uppercase tracking-[0.08em] text-[10px] text-text-dim ml-2">{{ t('rankings.sagaPoints') }}</span>
          </div>
        </div>
        <p class="font-body italic text-xs text-text-dim mb-6">
          {{ t('rankings.standing') }} <span class="text-text not-italic">{{ era.player.tier }}</span>
          <template v-if="era.player.nextTier">
            — {{ t('rankings.pointsFrom', { points: fmt(era.player.pointsToNext) }) }} <span class="text-gold">{{ era.player.nextTier }}</span>
          </template>
          <template v-else> — <span class="text-gold">{{ t('rankings.topOfLadder') }}</span></template>
        </p>
        <p class="font-body italic text-[11px] text-text-dim -mt-4 mb-6">
          {{ t('rankings.points.sources') }}
          {{ t('rankings.points.lossLead') }} <span class="text-error not-italic">{{ t('rankings.points.lossCost') }}</span> {{ t('rankings.points.lossTail') }}
          {{ t('rankings.points.payout') }}
          <span class="block mt-1 text-gold/80">{{ t('rankings.ragnarokNote') }}</span>
        </p>

        <!-- the ladder rail — horizontal on tablet/desktop -->
        <div class="rk-rail-wrap hidden sm:block">
          <div class="rk-rail">
            <div class="rk-rail-fill" :style="{ width: railPct + '%' }"></div>
            <!-- player marker -->
            <div class="rk-you" :style="{ left: railPct + '%' }" :title="t('rankings.youPoints', { points: fmt(era.player.rankPoints) })"></div>
            <!-- tier nodes -->
            <div
              v-for="(tr, ti) in ladder" :key="tr.tierName"
              class="rk-node" :class="{ 'rk-node-hit': reachedTier(tr.minPoints), 'rk-node-alt': ti % 2 === 1, 'rk-node-first': ti === 0, 'rk-node-last': ti === ladder.length - 1 }"
              :style="{ left: tierPctAt(ti) + '%' }"
            >
              <span class="rk-node-diamond"></span>
              <span class="rk-node-name" :class="reachedTier(tr.minPoints) ? 'text-gold' : 'text-text-dim'">{{ tr.tierName }}</span>
              <span class="rk-node-req tabular-nums">{{ fmt(tr.minPoints) }}</span>
              <!-- tier reward tooltip -->
              <span class="rk-node-tip">
                <b class="text-gold">{{ tr.tierName }}</b>
                <span v-if="tr.promotionSoft || tr.promotionPremium" class="block">
                  {{ t('rankings.tier.onPromotion') }} {{ fmt(tr.promotionSoft) }} {{ t('rankings.tier.silver') }}<template v-if="tr.promotionPremium"> + {{ tr.promotionPremium }} runes</template>
                </span>
                <span v-if="tr.rewardSoft" class="block">{{ fmt(tr.rewardSoft) }} {{ t('rankings.tier.silver') }}</span>
                <span v-if="tr.rewardPremium" class="block text-gold-bright">{{ tr.rewardPremium }} runes</span>
                <span v-if="tr.rewardXp" class="block text-purple-light">{{ fmt(tr.rewardXp) }} XP</span>
                <span class="block italic text-text-dim">{{ t('rankings.tier.paidAtClose') }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- mobile: the same ladder as a vertical list (no sideways scroll) -->
        <ul class="rk-vlad sm:hidden">
          <li
            v-for="tr in ladder" :key="'v-' + tr.tierName"
            class="rk-vrow" :class="{ 'rk-vrow-cur': tr.tierName === era.player.tier }"
          >
            <span class="rk-vdot" :class="{ 'rk-vdot-hit': reachedTier(tr.minPoints) }"></span>
            <div class="min-w-0 flex-1">
              <div class="rk-vname" :class="reachedTier(tr.minPoints) ? 'text-gold' : 'text-text-dim'">
                {{ tr.tierName }}
                <span v-if="tr.tierName === era.player.tier" class="rk-vyou">{{ t('rankings.you') }}</span>
              </div>
              <div v-if="tr.promotionSoft || tr.promotionPremium" class="rk-vrew">
                <span class="text-gold">{{ t('rankings.tier.now') }} {{ fmt(tr.promotionSoft) }} {{ t('rankings.tier.silver') }}</span>
                <span v-if="tr.promotionPremium" class="text-gold-bright">+ {{ tr.promotionPremium }} runes</span>
              </div>
              <div v-if="tr.rewardSoft || tr.rewardPremium || tr.rewardXp" class="rk-vrew">
                <span v-if="tr.rewardSoft">{{ fmt(tr.rewardSoft) }} {{ t('rankings.tier.silver') }}</span>
                <span v-if="tr.rewardPremium" class="text-gold-bright">· {{ tr.rewardPremium }} runes</span>
                <span v-if="tr.rewardXp" class="text-purple-light">· {{ fmt(tr.rewardXp) }} XP</span>
                <span class="italic">{{ t('rankings.tier.atSagaClose') }}</span>
              </div>
            </div>
            <span class="rk-vreq tabular-nums">{{ fmt(tr.minPoints) }}</span>
          </li>
        </ul>
      </div>

      <!-- ── LEADERBOARD — a ladder of standing stones ────────────────────── -->
      <div>
        <div class="flex items-center justify-between gap-3 mb-3">
          <div class="flex items-baseline gap-3">
            <h2 class="font-heading text-xl text-gold">{{ t('rankings.ledger') }}</h2>
            <span class="h-px w-24 self-center" style="background:linear-gradient(90deg,var(--color-border),transparent)"></span>
          </div>
          <div v-if="board" class="rk-chip">
            {{ t('rankings.yourMark') }} <b class="text-gold ml-1">#{{ board.me.rank }}</b><span class="text-text-dim ml-1">{{ t('rankings.of', { n: fmt(board.me.total) }) }}</span>
          </div>
        </div>

        <!-- category tabs -->
        <div v-if="board" class="flex flex-wrap gap-1 mb-5 border-b border-border/60">
          <button
            v-for="c in board.categories" :key="c.key"
            @click="loadBoard(c.key)"
            :class="['notch-tab', category === c.key && 'notch-tab-active']"
          >{{ c.label }}</button>
        </div>

        <p v-if="boardLoading" class="font-body text-text-dim text-sm">{{ t('rankings.boardLoading') }}</p>

        <template v-else-if="board">
          <div v-if="podium.length || ledger.length" class="rk-ladder">
            <!-- TOP THREE — the tallest stones, rank order top to bottom -->
            <div
              v-for="row in podium" :key="row.userId"
              class="rk-line" :class="[stoneClass(row.rank), { 'rk-line-me': row.isMe }]"
            >
              <span class="rk-line-rank">
                <img v-if="rankMedal(row.rank)" :src="rankMedal(row.rank)" :alt="'Rank ' + row.rank" class="rk-line-medal" />
                <span v-else class="rk-line-num">{{ String(row.rank).padStart(2, '0') }}</span>
              </span>
              <span class="rk-line-name">
                <span v-if="row.tag" class="rk-clan-tag">{{ row.tag }}</span>{{ row.username ?? t('rankings.unknown') }}
                <span v-if="row.isMe" class="rk-you-flag">{{ t('rankings.you') }}</span>
              </span>
              <span class="rk-line-score">
                <b class="tabular-nums">{{ fmt(row.value) }}</b>
                <i>{{ activeCatLabel }}</i>
              </span>
            </div>

            <!-- THE SETTLED GROUND — every stone below the podium, one height -->
            <div
              v-for="row in ledger" :key="row.userId"
              class="rk-line rk-line--settled" :class="{ 'rk-line-me': row.isMe }"
            >
              <span class="rk-line-rank"><span class="rk-line-num">{{ String(row.rank).padStart(2, '0') }}</span></span>
              <span class="rk-line-name">
                <span v-if="row.tag" class="rk-clan-tag">{{ row.tag }}</span>{{ row.username ?? t('rankings.unknown') }}
                <span v-if="row.isMe" class="rk-you-flag">{{ t('rankings.you') }}</span>
              </span>
              <span class="rk-line-score">
                <b class="tabular-nums">{{ fmt(row.value) }}</b>
                <i>{{ activeCatLabel }}</i>
              </span>
            </div>

            <!-- below the fold: the ladder skips ahead, then me between my
                 immediate neighbours — …, 635, 636 (you), 637 -->
            <template v-if="around.length">
              <div class="rk-skip" :aria-label="t('rankings.omitted')"><span class="rk-skip-dots">&#8943;</span></div>
              <div
                v-for="row in around" :key="row.userId"
                class="rk-line rk-line--settled" :class="{ 'rk-line-me': row.isMe }"
              >
                <span class="rk-line-rank"><span class="rk-line-num">{{ String(row.rank).padStart(2, '0') }}</span></span>
                <span class="rk-line-name">
                  <span v-if="row.tag" class="rk-clan-tag">{{ row.tag }}</span>{{ row.username ?? t('rankings.unknown') }}
                  <span v-if="row.isMe" class="rk-you-flag">{{ t('rankings.you') }}</span>
                </span>
                <span class="rk-line-score">
                  <b class="tabular-nums">{{ fmt(row.value) }}</b>
                  <i>{{ activeCatLabel }}</i>
                </span>
              </div>
            </template>

            <!-- on the board's roll but with nothing to show for it yet -->
            <template v-else-if="unranked && board.top.length">
              <div class="rk-skip" aria-hidden="true"><span class="rk-skip-dots">&#8943;</span></div>
              <div class="rk-line rk-line--settled rk-line-me">
                <span class="rk-line-rank"><span class="rk-line-num">&mdash;</span></span>
                <span class="rk-line-name">{{ board.me.username || t('rankings.yourMark') }} <span class="rk-you-flag">{{ t('rankings.you') }}</span></span>
                <span class="rk-line-score"><b class="tabular-nums">{{ fmt(board.me.value) }}</b><i>{{ activeCatLabel }}</i></span>
              </div>
            </template>
          </div>

          <p v-if="board.top.length === 0" class="font-body italic text-text-dim text-sm">
            {{ t('rankings.emptyBoard') }}
          </p>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* clan mark before the name — the whole point of a clan (QA item 8) */
.rk-clan-tag {
  display: inline-block; margin-right: 6px; padding: 1px 6px;
  font-family: var(--font-label, sans-serif); font-weight: 700; font-size: 10px;
  letter-spacing: 0.07em; color: var(--color-gold-bright, #e8d9a8);
  background: linear-gradient(180deg, #2a3140, #161c26);
  clip-path: var(--clip-hewn);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.14);
  vertical-align: 1px;
}

.rk-chip {
  display: inline-flex; align-items: baseline;
  padding: 4px 11px;
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-text-dim);
  border: 1px solid var(--color-border); border-radius: 0;
  background: rgba(255,255,255,0.02);
}

/* the doom clock — Ragnarök counts down in embers, not ledger-grey */
.rk-chip-doom {
  color: var(--color-gold-bright, #e8d9a8);
  border-color: rgba(196, 92, 60, 0.55);
  background: linear-gradient(180deg, rgba(120, 44, 28, 0.28), rgba(30, 14, 10, 0.35));
  text-shadow: 0 0 8px rgba(214, 106, 60, 0.35);
}

.rk-era {
  padding: 1.2rem 1.4rem 3.4rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  border-radius: 0;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(200,162,76,0.05), transparent 60%),
    var(--color-surface);
}

/* ── ladder rail (era tiers) ── */
.rk-rail-wrap { padding: 2.6rem 2.2rem 0.4rem; }
.rk-rail {
  position: relative; height: 4px; border-radius: 2px;
  background: var(--color-bg); border: 1px solid var(--color-border);
}
.rk-rail-fill {
  position: absolute; inset: 0 auto 0 0; border-radius: 2px;
  background: linear-gradient(90deg, var(--color-gold), var(--color-gold-bright));
  box-shadow: 0 0 8px rgba(227,200,120,0.35);
  transition: width 700ms cubic-bezier(0.3, 0.8, 0.3, 1);
}
.rk-you {
  position: absolute; top: 50%; width: 13px; height: 13px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: radial-gradient(circle at 35% 30%, #fff6dc, var(--color-gold-bright) 55%, var(--color-gold));
  border: 1px solid rgba(20,12,4,0.8); border-radius: 2px;
  box-shadow: 0 0 10px rgba(227,200,120,0.8);
  z-index: 3;
  animation: rk-you-pulse 2.4s ease-in-out infinite;
}
@keyframes rk-you-pulse {
  0%, 100% { box-shadow: 0 0 8px rgba(227,200,120,0.6); }
  50% { box-shadow: 0 0 16px rgba(227,200,120,1); }
}
.rk-node { position: absolute; top: 50%; z-index: 2; }
.rk-node-diamond {
  position: absolute; top: 0; left: 0; width: 8px; height: 8px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: var(--color-surface-2); border: 1px solid rgba(200,162,76,0.4); border-radius: 1px;
}
.rk-node-hit .rk-node-diamond { background: var(--color-gold); border-color: var(--color-gold-bright); }
.rk-node-name {
  position: absolute; top: 10px; left: 0; transform: translateX(-50%);
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase;
  white-space: nowrap;
}
.rk-node-req {
  position: absolute; top: 24px; left: 0; transform: translateX(-50%);
  font-family: var(--font-body, serif); font-size: 9px; color: #9db0c6;
  white-space: nowrap;
}
.rk-node-tip {
  position: absolute; bottom: 14px; left: 0; transform: translateX(-50%);
  min-width: 130px; padding: 7px 10px;
  font-family: var(--font-body, serif); font-size: 11px; line-height: 1.45; text-align: left;
  background: var(--color-surface-2); border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  border-radius: 0; box-shadow: 0 8px 20px rgba(0,0,0,0.6);
  opacity: 0; pointer-events: none; transition: opacity 150ms ease;
  z-index: 10;
}
.rk-node:hover .rk-node-tip { opacity: 1; }
/* zig-zag: every other tier labels ABOVE the rail so tight thresholds never collide */
.rk-node-alt .rk-node-name { top: auto; bottom: 10px; }
.rk-node-alt .rk-node-req { top: auto; bottom: 24px; }
.rk-node-alt .rk-node-tip { bottom: auto; top: 14px; }
/* edge nodes: anchor labels inward so they never spill or collide */
.rk-node-first .rk-node-name, .rk-node-first .rk-node-req { transform: none; left: -4px; text-align: left; }
.rk-node-last .rk-node-name, .rk-node-last .rk-node-req { transform: translateX(-100%); left: 4px; text-align: right; }

/* ── THE LADDER — a single column of standing stones, one per rank. The top
   of the board is the tallest stone; below the podium every stone settles to
   one height so a fifty-row board never tapers a stump to nothing (only ranks
   1–3 get extra height — see .rk-line--top1/2/3). Every stone shares the same
   grid template, so the rank glyph, the name and the score sit on one column
   measure and one right-hand axis top to bottom — nothing drifts. ───────── */
/* ── THE ROLL ─────────────────────────────────────────────────────────────────
   WAS twenty free-standing steles: every row with its own clip-path silhouette,
   its own stone texture and its own 16px drop-shadow, 72-108px tall, 10px apart.
   Three things were wrong with that. A stele is a TALL NARROW monument, and the
   same silhouette stretched across 1300px reads as a warped bulge — which is
   what the board actually looked like. Twenty monuments in a column stop being
   monuments. And at 72px a row, twenty names cost ~1800px of scrolling to carry
   three fields each.

   A leaderboard is a LEDGER: it exists to be scanned downward. So it is now one
   carved face with the entries incised into it as ruled lines — one texture, one
   shadow, one edge. Rows are 46px and plainly rectangular, so the rank column,
   the names and the figures each fall on a straight line the eye can run down.
   Standing matters more than any row's decoration, so the top three take gilt
   and a medal but keep the SAME height as everyone else: the scan column never
   kinks. */
.rk-ladder {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  clip-path: var(--clip-hewn);
  background:
    var(--tex-stone),
    linear-gradient(180deg, #333b46 0%, #262d37 45%, #1a2029 100%);
  background-size: 190px 190px, auto;
  background-blend-mode: soft-light, normal;
  filter: drop-shadow(0 10px 22px rgba(0, 0, 0, 0.55));
}
/* the carved rim as an inset ring — one for the whole roll, not one per row */
.rk-ladder::after {
  content: '';
  position: absolute; inset: 0;
  pointer-events: none;
  box-shadow:
    inset 0 1px 0 rgba(207, 232, 255, 0.16),
    inset 0 0 0 1px rgba(0, 0, 0, 0.5);
}

.rk-line {
  position: relative;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 14px;
  padding: 0 20px;
  min-height: 46px;
  /* the rule between entries is INCISED — a dark cut with a lit lower lip, the
     same device the beam and the equipment sockets use */
  box-shadow:
    inset 0 -1px 0 rgba(0, 0, 0, 0.55),
    inset 0 -2px 0 rgba(207, 232, 255, 0.045);
}
/* faint banding so a long roll stays trackable across its full width */
.rk-line:nth-child(even) { background: rgba(207, 232, 255, 0.022); }
.rk-line:last-child { box-shadow: none; }

/* THE TOP THREE — gilt, not height */
.rk-line--top1,
.rk-line--top2,
.rk-line--top3 { background: linear-gradient(90deg, rgba(227, 200, 120, 0.10), transparent 62%); }
.rk-line--top1 { background: linear-gradient(90deg, rgba(227, 200, 120, 0.17), transparent 68%); }

/* the player's own line — the one warm thing on a cold board. An ember rule down
   the left edge rather than the old glow halo, which bled onto its neighbours
   the moment the rows sat flush instead of 10px apart. */
.rk-line-me {
  background: linear-gradient(90deg, rgba(217, 135, 60, 0.20), rgba(217, 135, 60, 0.04) 55%, transparent);
}
.rk-line-me::before {
  content: '';
  position: absolute; left: 0; top: 0; bottom: 0;
  width: 3px;
  background: var(--color-ember);
  box-shadow: 0 0 10px color-mix(in srgb, var(--color-ember) 70%, transparent);
}

.rk-line-rank { display: flex; align-items: center; justify-content: flex-end; }
.rk-line-medal { width: 26px; height: 26px; object-fit: contain; filter: drop-shadow(0 0 6px rgba(227, 200, 120, 0.45)); }
.rk-line-num {
  font-family: var(--font-heading, serif); font-size: 15px; line-height: 1;
  color: #8296ad; font-variant-numeric: tabular-nums;
}
.rk-line-name {
  min-width: 0;
  display: flex; align-items: center; gap: 8px;
  font-family: var(--font-heading, serif); font-size: 15px;
  color: var(--color-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.rk-line--top1 .rk-line-name { font-size: 17px; color: var(--color-gold-bright); }
.rk-line--top2 .rk-line-name,
.rk-line--top3 .rk-line-name { color: var(--color-gold); }
.rk-line-me .rk-line-name {
  color: var(--color-aurora);
  text-shadow: 0 0 8px color-mix(in srgb, var(--color-aurora) 45%, transparent);
}

/* figure and unit share ONE line — stacking them was half the old row height */
.rk-line-score { display: flex; align-items: baseline; gap: 7px; white-space: nowrap; }
.rk-line-score b {
  font-family: var(--font-heading, serif); font-size: 16px;
  color: var(--color-gold); font-variant-numeric: tabular-nums;
}
.rk-line--top1 .rk-line-score b { font-size: 18px; color: var(--color-gold-bright); }
.rk-line-score i {
  font-family: var(--font-label); font-style: normal;
  font-size: 9px; letter-spacing: 0.09em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.rk-you-flag {
  flex: none;
  padding: 2px 7px;
  clip-path: var(--clip-hewn);
  font-family: var(--font-label, sans-serif); font-size: 8.5px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-bg); background: var(--color-ember);
}

.rk-skip {
  display: flex; align-items: center; justify-content: center;
  height: 22px;
  /* the roll jumps: a sunken band cut across the face, not a floating gap —
     with the rows now flush this has to read as part of the same stone */
  background: rgba(0, 0, 0, 0.28);
  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.6),
    inset 0 -1px 0 rgba(207, 232, 255, 0.05);
}
.rk-skip-dots {
  font-family: var(--font-heading, serif); font-size: 16px; line-height: 1;
  letter-spacing: 0.15em;
  color: color-mix(in srgb, var(--color-gold) 55%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  .rk-you { animation: none; }
  .rk-rail-fill { transition: none; }
}

/* ── vertical tier ladder (mobile <640px) — the horizontal rail can't fit
   the tier labels in one screen width, so on phones we list them top-down
   and the page only ever scrolls vertically. ── */
/* hidden by default (scoped rule beats Tailwind's sm:hidden); shown <640px below */
.rk-vlad { display: none; flex-direction: column; margin-top: 0.5rem; }
@media (max-width: 639.98px) { .rk-vlad { display: flex; } }
.rk-vrow {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 4px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 55%, transparent);
}
.rk-vrow:last-child { border-bottom: 0; }
.rk-vrow-cur {
  background: rgba(200, 162, 76, 0.06);
  border-left: 2px solid var(--color-gold);
  padding-left: 8px; margin-inline: -4px; padding-right: 8px;
}
.rk-vdot {
  flex: none; width: 11px; height: 11px; transform: rotate(45deg);
  border: 1px solid var(--color-border); background: rgba(0, 0, 0, 0.4);
}
.rk-vdot-hit { background: var(--color-gold); border-color: var(--color-gold-bright); }
.rk-vname {
  font-family: var(--font-heading, serif); font-size: 14px; letter-spacing: 0.02em;
  display: flex; align-items: center; gap: 8px;
}
.rk-vyou {
  font-family: var(--font-label, sans-serif); font-size: 8px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 2px 6px; color: var(--color-bg); background: var(--color-ember);
  clip-path: var(--clip-hewn);
}
.rk-vrew {
  font-family: var(--font-body, serif); font-size: 11px; color: var(--color-text-dim);
  margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.rk-vreq {
  flex: none; font-family: var(--font-heading, serif); font-size: 13px;
  color: var(--color-text-dim); font-variant-numeric: tabular-nums;
}

/* ── MOBILE ── stones lose their side padding a touch and the rank/score
   columns narrow — the grid template stays identical in KIND (three columns,
   score right-aligned) so nothing drifts, it just gets tighter. */
@media (max-width: 700px) {
  .rk-era { padding-inline: 1rem; }
  .rk-line { grid-template-columns: 44px 1fr 92px; padding: 0 14px; column-gap: 10px; }
  .rk-line-name { font-size: 15px; }
  .rk-line--top1 .rk-line-name { font-size: 17px; }
  .rk-line-score b { font-size: 15px; }
  .rk-line--top1 .rk-line-score b { font-size: 18px; }
}
</style>
